// =====================================================================
// EGL v12 Frontend - RE-AUTHENTICATION MODAL
// Purpose: Secure re-authentication for critical operations
// =====================================================================

import React, { useState, useEffect } from 'react'
import './ReAuthModal.css'
import { TokenManager } from '../lib/tokenManager'

interface ReAuthModalProps {
  isOpen: boolean
  username: string
  onSuccess: (token: string) => void
  onCancel: () => void
  onRedirectToLogin: () => void
}

interface ReAuthState {
  password: string
  isAuthenticating: boolean
  error: string | null
  success: string | null
  showRedirectButton: boolean
}

export const ReAuthModal: React.FC<ReAuthModalProps> = ({
  isOpen,
  username,
  onSuccess,
  onCancel,
  onRedirectToLogin
}) => {
  const [authState, setAuthState] = useState<ReAuthState>({
    password: '',
    isAuthenticating: false,
    error: null,
    success: null,
    showRedirectButton: false
  })

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setAuthState({
        password: '',
        isAuthenticating: false,
        error: null,
        success: null,
        showRedirectButton: false
      })
    }
  }, [isOpen])

  // Focus password field when modal opens
  useEffect(() => {
    if (isOpen) {
      const passwordField = document.getElementById('reauth-password')
      if (passwordField) {
        setTimeout(() => passwordField.focus(), 100)
      }
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!authState.password.trim()) {
      setAuthState(prev => ({ ...prev, error: 'Password is required' }))
      return
    }

    setAuthState(prev => ({ 
      ...prev, 
      isAuthenticating: true, 
      error: null,
      showRedirectButton: false 
    }))

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: username,
          password: authState.password
        })
      })

      const result = await response.json()
      


      if (result.success && result.data?.token) {
        // Store new token IMMEDIATELY to prevent SessionManager from detecting expiry
        localStorage.setItem('authToken', result.data.token)
        // Also update TokenManager cache
        TokenManager.setToken(result.data.token)
        
        // Show brief success message before closing
        setAuthState(prev => ({
          ...prev,
          isAuthenticating: false,
          error: null,
          success: 'Authentication successful! Proceeding with upload...',
          showRedirectButton: false
        }))
        
        // Call success callback with new token (this will close the modal)
        setTimeout(() => {
          onSuccess(result.data.token)
        }, 1500)
      } else {
        // Authentication failed
        setAuthState(prev => ({
          ...prev,
          isAuthenticating: false,
          error: result.message || 'Authentication failed. Please check your password.',
          success: null, // Clear any success message
          showRedirectButton: true
        }))
      }
    } catch (error) {
      console.error('Re-authentication error:', error)
      setAuthState(prev => ({
        ...prev,
        isAuthenticating: false,
        error: 'Network error. Please try again.',
        showRedirectButton: true
      }))
    }
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAuthState(prev => ({ 
      ...prev, 
      password: e.target.value,
      error: null, // Clear error when user types
      success: null // Clear success when user types
    }))
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !authState.isAuthenticating) {
      handleSubmit(e as any)
    }
  }

  if (!isOpen) return null

  return (
    <div className="reauth-modal-overlay">
      <div className="reauth-modal">
        <div className="reauth-header">
          <h3>🔐 Authentication Required</h3>
          <p>Critical operation requires authentication</p>
        </div>

        <form onSubmit={handleSubmit} className="reauth-form">
          <div className="reauth-field">
            <label htmlFor="reauth-username">Username</label>
            <input
              id="reauth-username"
              type="text"
              value={username}
              disabled
              className="reauth-input reauth-input-disabled"
            />
          </div>

          <div className="reauth-field">
            <label htmlFor="reauth-password">Password</label>
            <input
              id="reauth-password"
              type="password"
              value={authState.password}
              onChange={handlePasswordChange}
              onKeyPress={handleKeyPress}
              disabled={authState.isAuthenticating}
              className="reauth-input"
              placeholder="Enter your password"
              autoComplete="current-password"
            />
          </div>

          {authState.error && (
            <div className="reauth-error">
              ❌ {authState.error}
            </div>
          )}

          {authState.success && (
            <div className="reauth-success">
              ✅ {authState.success}
            </div>
          )}
          


          <div className="reauth-actions">
            {!authState.showRedirectButton ? (
              <>
                <button
                  type="submit"
                  disabled={authState.isAuthenticating || !authState.password.trim()}
                  className="reauth-submit-btn"
                >
                  {authState.isAuthenticating ? (
                    <>
                      <span className="reauth-spinner"></span>
                      Authenticating...
                    </>
                  ) : (
                    'Submit & Upload'
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={authState.isAuthenticating}
                  className="reauth-cancel-btn"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onRedirectToLogin}
                  className="reauth-redirect-btn"
                >
                  Redirect to Login Page
                </button>
                
                <button
                  type="button"
                  onClick={onCancel}
                  className="reauth-cancel-btn"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </form>

        <div className="reauth-security-note">
          <small>🛡️ This ensures your session is secure for critical operations</small>
        </div>
      </div>
    </div>
  )
} 